/**
 * Cached Exotel agent calling availability (Calling Paused toggle).
 * Backend: GET/PUT /api/exotel/agent-availability
 * Default ON when unknown or API not deployed — only block after explicit OFF from server.
 *
 * Outbound must check the recruiter who owns the From DID (callerId), not only
 * the logged-in user. Admin ATS + fallback DID can otherwise ring SH-265 while
 * that row is calls_available=false.
 */

import adminConfigService from '@/services/admin-config-service'
import { findDidAssignmentRow, resolveEmpIdByCallerId } from '@/utils/exotel-call-helpers'

/** @type {{ emp_id: string|null, calls_available: boolean }|null} */
let cache = null
let inflight = null

/** @type {Record<string, { emp_id: string, calls_available: boolean }>} */
const byEmpId = Object.create(null)
/** @type {Record<string, Promise<object>>} */
const inflightByEmpId = Object.create(null)

const AVAILABILITY_EVENT = 'exotel-agent-availability-changed'

const UNAVAILABLE_MESSAGE =
  'Calling is paused. Outbound is blocked while unavailable.'

function loggedInEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    return userData && userData.emp_id != null && String(userData.emp_id) !== ''
      ? String(userData.emp_id)
      : null
  } catch (e) {
    return null
  }
}

function normalizePayload(data) {
  if (!data || typeof data !== 'object') return null
  const inner = (
    data.data
    && typeof data.data === 'object'
    && ('calls_available' in data.data || 'callsAvailable' in data.data)
  )
    ? data.data
    : data
  const avail = inner.calls_available ?? inner.callsAvailable
  if (avail === undefined || avail === null) return null
  return {
    emp_id: inner.emp_id != null ? String(inner.emp_id) : (inner.empId != null ? String(inner.empId) : null),
    calls_available: avail === true || avail === 1 || avail === 'true' || avail === '1',
  }
}

function defaultCache() {
  return { emp_id: null, calls_available: true }
}

function emitAvailabilityChange() {
  if (typeof window === 'undefined' || !cache) return
  window.dispatchEvent(new CustomEvent(AVAILABILITY_EVENT, { detail: { ...cache } }))
}

function rememberEmpRow(row) {
  if (!row || row.emp_id == null || String(row.emp_id) === '') return
  byEmpId[String(row.emp_id)] = {
    emp_id: String(row.emp_id),
    calls_available: row.calls_available !== false,
  }
}

function applyCurrentUserRow(row) {
  cache = row
  rememberEmpRow(row)
  emitAvailabilityChange()
  return cache
}

function isExplicitlyUnavailable(row) {
  return row != null && row.calls_available === false
}

function didRowExplicitlyUnavailable(row) {
  if (!row) return false
  const avail = row.calls_available ?? row.callsAvailable
  if (avail === undefined || avail === null) return false
  return !(avail === true || avail === 1 || avail === 'true' || avail === '1')
}

function unavailableEnvelope() {
  return {
    ok: false,
    response: {
      status: 403,
      data: {
        message: UNAVAILABLE_MESSAGE,
        code: 'CALLS_UNAVAILABLE',
      },
    },
  }
}

/** Sync read: false only when explicitly OFF after load; true when unknown or ON. */
export function getCallsAvailableOrDefault() {
  if (!cache) return true
  return cache.calls_available !== false
}

/** Sync read: null if not loaded yet; otherwise the cached boolean. */
export function getCachedCallsAvailable() {
  if (!cache) return null
  return cache.calls_available === true
}

/** True when availability is loaded and explicitly OFF. */
export function isCallingPaused() {
  return cache != null && cache.calls_available === false
}

/**
 * Update cache from FCM or another tab without PUT (e.g. inbound_call_unavailable).
 * @param {boolean} callsAvailable
 */
export function applyRemoteAvailability(callsAvailable) {
  cache = { ...(cache || defaultCache()), calls_available: callsAvailable !== false }
  rememberEmpRow(cache)
  emitAvailabilityChange()
  return cache
}

/** Subscribe to cache updates (toggle, FCM, reload). Returns unsubscribe fn. */
export function onAvailabilityChange(fn) {
  if (typeof window === 'undefined') return () => {}
  const handler = e => { if (e.detail) fn(e.detail) }
  window.addEventListener(AVAILABILITY_EVENT, handler)
  return () => window.removeEventListener(AVAILABILITY_EVENT, handler)
}

async function fetchAvailabilityRow(empId) {
  const res = await adminConfigService.getAgentAvailability(empId)
  if (res && typeof res.status === 'number' && res.status >= 400) {
    return null
  }
  return normalizePayload(res) || normalizePayload(res && res.data)
}

/**
 * Load availability. With empId, GET ?emp_id= for that agent and do not overwrite
 * the logged-in user's toggle cache unless the row is theirs.
 * @param {{ force?: boolean, empId?: string|null }} [opts]
 */
export async function loadAgentAvailability({ force = false, empId = null } = {}) {
  const requested = empId != null && String(empId) !== '' ? String(empId) : null

  if (requested) {
    if (!force && byEmpId[requested]) return byEmpId[requested]
    if (!force && inflightByEmpId[requested]) return inflightByEmpId[requested]

    inflightByEmpId[requested] = (async () => {
      try {
        const normalized = await fetchAvailabilityRow(requested)
        if (normalized && String(normalized.emp_id) === requested) {
          rememberEmpRow(normalized)
          const me = loggedInEmpId()
          if (me && me === requested) applyCurrentUserRow(normalized)
          return normalized
        }
        // Query ignored or different user — do not treat as the owner's ON.
        return {
          emp_id: requested,
          calls_available: null,
          mismatch: true,
          returned_emp_id: normalized && normalized.emp_id ? String(normalized.emp_id) : null,
        }
      } catch (e) {
        return { emp_id: requested, calls_available: null, error: true }
      } finally {
        delete inflightByEmpId[requested]
      }
    })()

    return inflightByEmpId[requested]
  }

  if (!force && cache) return cache
  if (!force && inflight) return inflight

  inflight = (async () => {
    try {
      const res = await adminConfigService.getAgentAvailability()
      if (res && typeof res.status === 'number' && res.status === 404) {
        return applyCurrentUserRow(defaultCache())
      }
      if (res && typeof res.status === 'number' && res.status >= 400) {
        return applyCurrentUserRow(defaultCache())
      }
      const normalized = normalizePayload(res) || normalizePayload(res && res.data)
      return applyCurrentUserRow(normalized || defaultCache())
    } catch (e) {
      return applyCurrentUserRow(defaultCache())
    } finally {
      inflight = null
    }
  })()

  return inflight
}

/**
 * Optimistic update + PUT. Reverts cache on failure.
 * Writes the logged-in user's own flag only (API ignores body emp_id).
 * @param {boolean} callsAvailable
 */
export async function setAgentAvailability(callsAvailable) {
  const prev = cache
  cache = { ...(cache || defaultCache()), calls_available: !!callsAvailable }

  try {
    const res = await adminConfigService.setAgentAvailability({
      calls_available: !!callsAvailable,
    })

    if (res && typeof res.status === 'number' && res.status >= 400) {
      cache = prev
      const err = new Error(
        (res.data && (res.data.message || res.data.error))
        || 'Failed to update calling availability',
      )
      err.response = res
      throw err
    }

    if (res && (res.status === 'ERROR' || res.status === 'FAILED')) {
      cache = prev
      throw new Error(res.message || (res.data && res.data.message) || 'Failed to update calling availability')
    }

    const normalized = normalizePayload(res) || normalizePayload(res && res.data)
    if (normalized) applyCurrentUserRow(normalized)
    else applyCurrentUserRow({ ...(cache || defaultCache()), calls_available: !!callsAvailable })
    return cache
  } catch (e) {
    if (cache !== prev && !(e && e.response)) {
      cache = prev
    }
    throw e
  }
}

async function loadExotelConfigList() {
  try {
    const res = await adminConfigService.getExotelConfig()
    if (res && res.status === 'OK' && Array.isArray(res.data)) return res.data
    if (Array.isArray(res)) return res
    if (res && Array.isArray(res.data)) return res.data
  } catch (e) {
    /* config optional — owner lookup skipped */
  }
  return []
}

/**
 * Ensure availability is loaded for the logged-in user and the From-DID owner.
 * If either is explicitly OFF, return a synthetic make-call error envelope.
 * @param {{ callerId?: string, caller_id?: string, exotelConfig?: Array }} [callData]
 * @returns {Promise<{ ok: true } | { ok: false, response: object }>}
 */
export async function assertCallsAvailableForDial(callData = {}) {
  const callerId = (callData && (callData.callerId || callData.caller_id)) || null
  let config = callData && Array.isArray(callData.exotelConfig) ? callData.exotelConfig : null
  if (!config) config = await loadExotelConfigList()

  const ownerEmpId = resolveEmpIdByCallerId(config, callerId)
  const ownerRow = findDidAssignmentRow(config, callerId)
  if (didRowExplicitlyUnavailable(ownerRow)) {
    return unavailableEnvelope()
  }

  await loadAgentAvailability({ force: true })
  if (isExplicitlyUnavailable(cache)) {
    return unavailableEnvelope()
  }

  const me = loggedInEmpId()
  if (ownerEmpId && (!me || ownerEmpId !== me)) {
    const owner = await loadAgentAvailability({ force: true, empId: ownerEmpId })
    if (isExplicitlyUnavailable(owner)) {
      return unavailableEnvelope()
    }
  }

  return { ok: true }
}

export const CALLS_UNAVAILABLE_MESSAGE = UNAVAILABLE_MESSAGE
